import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

export default function FireDamageRestorationPage() {
  useEffect(() => {
    window.scrollTo(0, 0);

    const SITE_URL = 'https://mercedwaterrestoration.com';

    document.title = 'Fire Damage Restoration Merced CA | Smoke Odor & Full Recovery | Indoor-Restore';

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute(
      'content',
      'Fire damage restoration in Merced, CA goes beyond what you can see. Learn how Indoor-Restore Services removes smoke odor, cleans soot, and restores homes to pre-loss condition after fire damage.'
    );

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute(
      'content',
      'fire damage restoration Merced CA, smoke odor removal, soot cleanup, fire damage recovery, smoke damage restoration Central Valley'
    );

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${SITE_URL}/blog/fire-damage-restoration-smoke-odor-recovery`);

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": "Fire Damage Restoration: From Smoke Odor to Full Recovery",
      "description": "Fire damage goes beyond what you can see. Smoke and soot penetrate walls, HVAC systems, and furniture. Learn how Indoor-Restore Services restores Merced homes to pre-loss condition.",
      "image": "https://public.readdy.ai/ai/img_res/d2475ef9353fd23bac43c20e53dd5552.jpg",
      "datePublished": "2025-03-22T08:00:00-07:00",
      "dateModified": "2025-03-22T08:00:00-07:00",
      "author": {
        "@type": "Organization",
        "name": "Indoor-Restore Services",
        "url": SITE_URL
      },
      "publisher": {
        "@type": "Organization",
        "name": "Indoor-Restore Services",
        "logo": {
          "@type": "ImageObject",
          "url": "https://mercedwaterrestoration.com/wp-content/uploads/2024/11/Indoor-Restore-Services-Logo.png"
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `${SITE_URL}/blog/fire-damage-restoration-smoke-odor-recovery`
      }
    });
    document.head.appendChild(script);

    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How long does fire damage restoration take in Merced, CA?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The timeline depends on the severity of the fire. Minor smoke and soot damage can be cleaned and restored in 3–7 days. Moderate fire damage affecting multiple rooms typically takes 2–4 weeks. Severe structural fire damage requiring reconstruction can take 1–3 months or longer. Indoor-Restore Services provides a detailed timeline estimate after the initial assessment."
          }
        },
        {
          "@type": "Question",
          "name": "Does homeowner's insurance cover fire damage restoration in California?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Standard homeowner's insurance policies in California cover fire damage restoration, including smoke and soot cleanup, odor removal, structural repairs, and contents restoration. Most policies also cover temporary housing (ALE — Additional Living Expenses) while your home is being restored. Indoor-Restore Services works directly with all major insurance carriers and provides detailed documentation to support your claim."
          }
        },
        {
          "@type": "Question",
          "name": "Can smoke odor be completely removed from a home?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, with professional-grade equipment and techniques. Consumer air fresheners and store-bought odor eliminators only mask smoke odor temporarily. Professional restoration companies use thermal fogging, hydroxyl generators, and ozone treatment to neutralize smoke molecules at the molecular level. HVAC systems, ductwork, insulation, and soft contents must also be treated or replaced to achieve complete odor elimination."
          }
        },
        {
          "@type": "Question",
          "name": "Is it safe to stay in my Merced home after a fire?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "In most cases, no — at least not immediately. Smoke and soot contain toxic chemicals including carbon monoxide, formaldehyde, benzene, and heavy metals that pose serious health risks. Structural integrity may also be compromised. Indoor-Restore Services conducts a thorough safety assessment before any restoration work begins and can help you navigate your insurance policy's Additional Living Expenses (ALE) coverage for temporary housing."
          }
        }
      ]
    });
    document.head.appendChild(faqScript);

    return () => {
      if (script.parentNode) document.head.removeChild(script);
      if (faqScript.parentNode) document.head.removeChild(faqScript);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-100 pt-24 pb-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-teal-600 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to="/blog" className="hover:text-teal-600 transition-colors">Blog</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-gray-900 font-medium">Fire &amp; Smoke Restoration</span>
          </nav>
        </div>
      </div>

      <article className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header */}
          <div className="mb-6">
            <span className="inline-block bg-orange-50 text-orange-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-4 uppercase tracking-wider">
              Fire &amp; Smoke
            </span>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
              Fire Damage Restoration: From Smoke Odor to Full Recovery
            </h1>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                March 22, 2025
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                6 min read
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-map-pin-line"></i>
                Merced, CA
              </span>
            </div>
          </div>

          {/* Featured Image */}
          <div className="w-full h-96 rounded-xl overflow-hidden mb-10 shadow-lg">
            <img
              src="https://readdy.ai/api/search-image?query=restoration%20technician%20cleaning%20smoke%20damaged%20wall%20in%20residential%20home%20after%20fire%20professional%20equipment%20protective%20gear%20interior%20photography&width=1200&height=630&seq=blog6-indoor&orientation=landscape"
              alt="Fire damage restoration technician cleaning smoke-damaged walls in Merced home"
              title="Fire Damage Restoration Merced CA - Smoke Odor Removal & Full Recovery"
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Article Body */}
          <div className="prose prose-lg max-w-none">

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              A house fire is one of the most traumatic events a homeowner can experience. But once the flames are out and the fire department leaves, many Merced families are shocked to discover that <strong>the visible fire damage is only part of the problem</strong>. Smoke, soot, and toxic residues penetrate deep into walls, ceilings, HVAC systems, furniture, and personal belongings — often causing more long-term damage than the fire itself.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              At <strong>Indoor-Restore Services</strong>, we've helped countless Merced County homeowners navigate the fire damage restoration process — from emergency board-up and smoke odor removal to full structural reconstruction. In this guide, we'll explain exactly what happens during professional fire damage restoration, why DIY cleanup falls short, and how to get your home back to pre-loss condition as quickly as possible.
            </p>

            {/* Emergency Alert */}
            <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-lg mb-8">
              <p className="text-sm text-orange-900 font-semibold mb-2">
                <i className="ri-alarm-warning-line mr-2"></i>IMPORTANT SAFETY NOTICE
              </p>
              <p className="text-orange-800 text-sm leading-relaxed">
                Do not re-enter your home after a fire until it has been cleared by the fire department and a licensed restoration professional. Smoke residue contains toxic chemicals, and structural integrity may be compromised. Call Indoor-Restore Services at <strong>(209) 451-2288</strong> for a safe, immediate assessment.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-2">
              <i className="ri-eye-line text-orange-500"></i>
              Why Fire Damage Goes Far Beyond What You Can See
            </h2>

            <p className="text-gray-700 leading-relaxed mb-4">
              Most homeowners focus on the charred, blackened areas after a fire — and understandably so. But <strong>smoke and soot travel far beyond the burn zone</strong>, infiltrating every corner of your home within minutes. Here's what's actually happening inside your walls and HVAC system:
            </p>

            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6 ml-4">
              <li><strong>Soot particles</strong> are microscopic and penetrate porous surfaces like drywall, wood, insulation, and fabric</li>
              <li><strong>Smoke odor molecules</strong> bond chemically to surfaces and cannot be removed with standard cleaning products</li>
              <li><strong>HVAC systems</strong> distribute smoke and soot throughout the entire home — even rooms untouched by flames</li>
              <li><strong>Toxic residues</strong> from burned plastics, synthetics, and building materials coat surfaces with carcinogens</li>
              <li><strong>Acidic soot</strong> begins corroding metal surfaces, appliances, and electronics within hours of a fire</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mb-6">
              In Merced's warm climate, these residues can also accelerate secondary damage. Heat and humidity cause soot to set deeper into porous materials, making professional fire damage restoration more difficult — and more expensive — the longer you wait.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-2">
              <i className="ri-list-check-2 text-orange-500"></i>
              The 6-Step Professional Fire Damage Restoration Process
            </h2>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-3">
              <span className="inline-flex items-center justify-center w-8 h-8 bg-orange-500 text-white rounded-full mr-3 text-sm">1</span>
              Emergency Assessment &amp; Board-Up
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              The first step is a thorough safety assessment. Our team evaluates structural integrity, identifies hazardous materials, and secures the property with emergency board-up and tarping services to prevent weather damage, vandalism, and unauthorized entry. We also document all damage with photos, moisture readings, and air quality measurements — critical evidence for your insurance claim.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-3">
              <span className="inline-flex items-center justify-center w-8 h-8 bg-orange-500 text-white rounded-full mr-3 text-sm">2</span>
              Water Removal &amp; Drying
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Firefighting efforts leave behind significant water damage. Before smoke and soot cleanup can begin, all standing water must be extracted and structural drying must be completed. Indoor-Restore Services deploys industrial extractors and commercial dehumidifiers to dry your home thoroughly — preventing mold growth that would compound the fire damage restoration costs.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-3">
              <span className="inline-flex items-center justify-center w-8 h-8 bg-orange-500 text-white rounded-full mr-3 text-sm">3</span>
              Soot &amp; Smoke Residue Removal
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              This is the most labor-intensive phase of fire damage restoration. Our IICRC-certified technicians use specialized dry and wet cleaning methods to remove soot from every surface — walls, ceilings, floors, cabinets, and structural elements. Different soot types require different cleaning approaches:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6 ml-4">
              <li><strong>Dry soot</strong> (from fast-burning fires) — removed with dry chemical sponges before wet cleaning</li>
              <li><strong>Wet soot</strong> (from slow, smoldering fires) — requires heavy-duty degreasers and agitation</li>
              <li><strong>Protein residue</strong> (from kitchen fires) — nearly invisible but extremely pungent, requires enzyme-based cleaners</li>
              <li><strong>Fuel oil soot</strong> — thick, sticky residue requiring specialized solvents</li>
            </ul>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-3">
              <span className="inline-flex items-center justify-center w-8 h-8 bg-orange-500 text-white rounded-full mr-3 text-sm">4</span>
              Smoke Odor Elimination
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Smoke odor is one of the most persistent challenges in fire damage restoration. <strong>Air fresheners and consumer odor eliminators simply don't work</strong> — they mask the smell temporarily while smoke molecules remain bonded to surfaces. Professional smoke odor removal requires:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6 ml-4">
              <li><strong>Thermal fogging</strong> — a deodorizing fog that penetrates the same areas smoke did, neutralizing odor molecules</li>
              <li><strong>Hydroxyl generators</strong> — produce hydroxyl radicals that break down odor-causing compounds at the molecular level</li>
              <li><strong>Ozone treatment</strong> — highly effective for severe smoke odor in unoccupied spaces</li>
              <li><strong>HVAC cleaning</strong> — ductwork, air handlers, and filters must be cleaned or replaced to prevent odor recirculation</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-6">
              In Merced's climate, where homes are often sealed tight against summer heat, smoke odor can become deeply embedded in insulation and wall cavities. Our team uses moisture meters and air quality monitors to verify complete odor elimination before moving to the next phase.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-3">
              <span className="inline-flex items-center justify-center w-8 h-8 bg-orange-500 text-white rounded-full mr-3 text-sm">5</span>
              Contents Cleaning &amp; Restoration
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Your belongings — furniture, clothing, electronics, documents, and personal items — can often be restored rather than replaced. Indoor-Restore Services uses ultrasonic cleaning, ozone chambers, and specialized textile cleaning to restore smoke-damaged contents. We create a detailed inventory of all items, noting what can be restored and what must be replaced, which directly supports your insurance claim for contents coverage.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-8 mb-3">
              <span className="inline-flex items-center justify-center w-8 h-8 bg-orange-500 text-white rounded-full mr-3 text-sm">6</span>
              Structural Reconstruction
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              The final phase of fire damage restoration is rebuilding what was lost. This may include replacing drywall, insulation, flooring, cabinetry, roofing, and structural framing. Indoor-Restore Services is a full-service restoration and reconstruction company — meaning we handle everything from initial cleanup through final reconstruction, so you don't have to coordinate multiple contractors. Our goal is to return your Merced home to pre-loss condition, or better.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-2">
              <i className="ri-shield-check-line text-orange-500"></i>
              Working with Your Insurance Company
            </h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              Standard homeowner's insurance policies in California cover fire damage restoration, including smoke cleanup, odor removal, structural repairs, and contents restoration. Most policies also include <strong>Additional Living Expenses (ALE)</strong> coverage — meaning your insurer will pay for temporary housing, meals, and other costs while your home is being restored.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              Indoor-Restore Services works directly with all major insurance carriers serving Merced County. We provide detailed estimates, photo documentation, moisture readings, and air quality reports that insurance adjusters require. Our team can communicate directly with your adjuster to streamline the claims process and ensure you receive the full coverage you're entitled to.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              <strong>Important:</strong> Your policy requires you to take reasonable steps to prevent further damage immediately after a fire. This means securing the property, removing standing water, and beginning mitigation — not waiting for an adjuster to visit. Failure to mitigate can result in denied coverage for secondary damage.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-2">
              <i className="ri-question-line text-orange-500"></i>
              Frequently Asked Questions
            </h2>

            <div className="space-y-4 mb-10">
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-100">
                <h3 className="text-base font-bold text-gray-900 mb-2">How long does fire damage restoration take in Merced, CA?</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  The timeline depends on severity. Minor smoke and soot damage can be cleaned in 3–7 days. Moderate damage affecting multiple rooms typically takes 2–4 weeks. Severe structural damage requiring reconstruction can take 1–3 months or longer. Indoor-Restore Services provides a detailed timeline estimate after the initial assessment.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6 border border-gray-100">
                <h3 className="text-base font-bold text-gray-900 mb-2">Does homeowner's insurance cover fire damage restoration in California?</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Yes. Standard homeowner's insurance covers fire damage restoration including smoke cleanup, soot removal, structural repairs, and contents restoration. Most policies also cover temporary housing (ALE) while your home is being restored. We work directly with all major carriers to support your claim.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6 border border-gray-100">
                <h3 className="text-base font-bold text-gray-900 mb-2">Can smoke odor be completely removed from a home?</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Yes, with professional-grade equipment. Consumer air fresheners only mask smoke odor temporarily. Professional restoration uses thermal fogging, hydroxyl generators, and ozone treatment to neutralize smoke molecules at the molecular level. HVAC systems and ductwork must also be cleaned or replaced for complete odor elimination.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6 border border-gray-100">
                <h3 className="text-base font-bold text-gray-900 mb-2">Is it safe to stay in my Merced home after a fire?</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  In most cases, no — at least not immediately. Smoke residue contains toxic chemicals including carbon monoxide, formaldehyde, and benzene. Structural integrity may also be compromised. We conduct a thorough safety assessment before restoration begins and can help you navigate your insurance policy's ALE coverage for temporary housing.
                </p>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4 flex items-center gap-2">
              <i className="ri-home-heart-line text-orange-500"></i>
              Your Path to Full Recovery Starts Here
            </h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              Fire damage restoration is a complex, multi-phase process that requires professional expertise, specialized equipment, and deep experience with insurance claims. Attempting DIY cleanup after a fire can expose your family to toxic residues, void your insurance coverage, and allow secondary damage to compound — ultimately costing far more than professional restoration.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              <strong>Indoor-Restore Services</strong> is Merced County's trusted fire damage restoration company. We're IICRC-certified, fully licensed and insured, and available 24/7 for emergency response. From the moment you call us, we'll guide you through every step — from emergency board-up and smoke odor removal to full structural reconstruction — so you can focus on your family while we restore your home.
            </p>

            <p className="text-gray-700 leading-relaxed">
              <strong>Dealing with fire or smoke damage right now?</strong> Call our 24/7 emergency line at <a href="tel:2094512288" className="text-teal-600 font-semibold hover:text-teal-700 underline">(209) 451-2288</a>. We serve Merced, Atwater, Los Banos, Livingston, and all of Merced County — and we'll be on-site within 60 minutes to begin your recovery.
            </p>
          </div>

          {/* Emergency CTA */}
          <div className="bg-gradient-to-br from-orange-600 to-orange-800 rounded-xl p-8 text-center mt-12">
            <div className="max-w-2xl mx-auto">
              <i className="ri-fire-line text-5xl text-orange-200 mb-4 block"></i>
              <h3 className="text-2xl font-bold text-white mb-3">
                Dealing with Fire or Smoke Damage?
              </h3>
              <p className="text-orange-100 mb-6 text-sm leading-relaxed">
                Our emergency response team is available 24/7 throughout Merced County. We'll be on-site within 60 minutes to secure your property and begin the restoration process.
              </p>
              <a
                href="tel:2094512288"
                className="inline-flex items-center gap-3 bg-white text-orange-700 px-8 py-4 rounded-lg font-bold text-lg hover:bg-orange-50 transition-colors whitespace-nowrap cursor-pointer shadow-lg"
              >
                <i className="ri-phone-line text-xl"></i>
                Call Now: (209) 451-2288
              </a>
              <p className="text-orange-200 text-xs mt-4">
                Available 24/7 &bull; 60-Minute Response &bull; Free Estimates
              </p>
            </div>
          </div>

          {/* Related Articles */}
          <div className="mt-16 pt-12 border-t border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Related Articles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Link
                to="/blog/water-damage-first-24-hours"
                onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                className="group bg-gray-50 rounded-lg overflow-hidden border border-gray-100 hover:border-teal-200 hover:shadow-md transition-all cursor-pointer block"
              >
                <div className="w-full h-40 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=flooded%20living%20room%20interior%20with%20water%20on%20hardwood%20floor%20furniture%20damaged%20homeowner%20assessing%20water%20damage%20natural%20light%20realistic%20photography&width=400&height=250&seq=blog1-indoor&orientation=landscape"
                    alt="What to Do in the First 24 Hours After Water Damage"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <span className="text-teal-600 text-xs font-semibold uppercase tracking-wide">Water Damage</span>
                  <h4 className="text-base font-bold text-gray-900 mt-1 mb-1 group-hover:text-teal-700 transition-colors leading-snug">
                    What to Do in the First 24 Hours After Water Damage
                  </h4>
                  <p className="text-gray-500 text-sm line-clamp-2">Acting fast is critical. Learn the immediate steps to minimize damage.</p>
                </div>
              </Link>

              <Link
                to="/blog/structural-drying-101"
                onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                className="group bg-gray-50 rounded-lg overflow-hidden border border-gray-100 hover:border-teal-200 hover:shadow-md transition-all cursor-pointer block"
              >
                <div className="w-full h-40 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=industrial%20air%20movers%20and%20dehumidifiers%20set%20up%20in%20empty%20room%20during%20water%20damage%20restoration%20process%20professional%20equipment%20clean%20space&width=400&height=250&seq=blog4-indoor&orientation=landscape"
                    alt="Structural Drying 101"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <span className="text-teal-600 text-xs font-semibold uppercase tracking-wide">Restoration Tips</span>
                  <h4 className="text-base font-bold text-gray-900 mt-1 mb-1 group-hover:text-teal-700 transition-colors leading-snug">
                    Structural Drying 101: Why Industrial Equipment Matters
                  </h4>
                  <p className="text-gray-500 text-sm line-clamp-2">Why professional drying equipment is essential to a full restoration.</p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
